import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  UserGroupIcon,
  OfficeBuildingIcon,
  CalendarIcon,
  ClipboardListIcon,
  TrendingUpIcon,
  CashIcon,
  ChartBarIcon,
} from "@heroicons/react/outline";

const DashboardHome = () => {
  const data = [
    { name: "January", revenue: 4000, sales: 2400, bookings: 2400 },
    { name: "February", revenue: 3000, sales: 1398, bookings: 2210 },
    { name: "March", revenue: 2000, sales: 9800, bookings: 2290 },
    { name: "April", revenue: 2780, sales: 3908, bookings: 2000 },
    { name: "May", revenue: 1890, sales: 4800, bookings: 2181 },
    { name: "June", revenue: 2390, sales: 3800, bookings: 2500 },
    { name: "July", revenue: 3490, sales: 4300, bookings: 2100 },
  ];

  const statsCards = [
    {
      title: "Total Users",
      value: "4",
      icon: UserGroupIcon,
      gradient: "from-blue-400 to-blue-600",
      bgGradient: "from-blue-50 to-blue-100",
      iconBg: "bg-blue-500",
      change: "+12%",
      changeType: "increase",
    },
    {
      title: "Total Properties",
      value: "5",
      icon: OfficeBuildingIcon,
      gradient: "from-emerald-400 to-emerald-600",
      bgGradient: "from-emerald-50 to-emerald-100",
      iconBg: "bg-emerald-500",
      change: "+8%",
      changeType: "increase",
    },
    {
      title: "Availabilities",
      value: "20",
      icon: CalendarIcon,
      gradient: "from-amber-400 to-amber-600",
      bgGradient: "from-amber-50 to-amber-100",
      iconBg: "bg-amber-500",
      change: "+15%",
      changeType: "increase",
    },
    {
      title: "Reservations",
      value: "15",
      icon: ClipboardListIcon,
      gradient: "from-purple-400 to-purple-600",
      bgGradient: "from-purple-50 to-purple-100",
      iconBg: "bg-purple-500",
      change: "+23%",
      changeType: "increase",
    },
  ];

  return (
    <div className="dashboard-home">
      {/* Welcome Section */}
      <div className="welcome-banner">
        <div className="welcome-content">
          <div>
            <h1 className="welcome-title">Welcome Back! 👋</h1>
            <p className="welcome-subtitle">
              Here's what's happening with your properties today
            </p>
          </div>
          <div className="welcome-badge">
            <TrendingUpIcon className="w-5 h-5" />
            <span>All Systems Active</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className={`stat-card-gradient bg-gradient-to-br ${stat.bgGradient}`}>
                <div className="stat-card-content">
                  <div className="stat-info">
                    <p className="stat-title">{stat.title}</p>
                    <h3 className="stat-value">{stat.value}</h3>
                    <div className="stat-change stat-increase">
                      <TrendingUpIcon className="w-4 h-4" />
                      <span>{stat.change} from last month</span>
                    </div>
                  </div>
                  <div className={`stat-icon ${stat.iconBg}`}>
                    <Icon className="w-8 h-8" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Revenue Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CashIcon className="w-5 h-5 text-purple-600" />
                <h3 className="chart-title">Revenue & Sales Overview</h3>
              </div>
              <p className="chart-subtitle">12.04.2022 - 12.05.2022</p>
            </div>
            <div className="chart-tabs">
              <button className="chart-tab chart-tab-active">Day</button>
              <button className="chart-tab">Week</button>
              <button className="chart-tab">Month</button>
            </div>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#8b5cf6"
                  strokeWidth={3}
                  dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                  fillOpacity={1}
                  fill="url(#colorSales)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bookings Bar Chart */}
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <ChartBarIcon className="w-5 h-5 text-indigo-600" />
                <h3 className="chart-title">Monthly Performance</h3>
              </div>
              <p className="chart-subtitle">Bookings & Revenue Analysis</p>
            </div>
          </div>

          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <defs>
                  <linearGradient id="barGradient1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={1} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={1} />
                  </linearGradient>
                  <linearGradient id="barGradient2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={1} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Bar 
                  dataKey="sales" 
                  fill="url(#barGradient1)" 
                  radius={[8, 8, 0, 0]}
                />
                <Bar 
                  dataKey="bookings" 
                  fill="url(#barGradient2)" 
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .dashboard-home {
          padding: 0;
        }

        .welcome-banner {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 1rem;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 10px 25px -5px rgba(102, 126, 234, 0.3);
          position: relative;
          overflow: hidden;
        }

        .welcome-banner::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
          border-radius: 50%;
          transform: translate(30%, -30%);
        }

        .welcome-content {
          position: relative;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .welcome-title {
          font-size: 2rem;
          font-weight: 800;
          color: white;
          margin: 0 0 0.5rem 0;
        }

        .welcome-subtitle {
          font-size: 1rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
        }

        .welcome-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 9999px;
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          position: relative;
          overflow: hidden;
          border-radius: 1rem;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .stat-card-gradient {
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.8);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .stat-card-gradient::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 200px;
          height: 200px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, transparent 70%);
          border-radius: 50%;
          transform: translate(40%, -40%);
        }

        .stat-card-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          position: relative;
        }

        .stat-info {
          flex: 1;
        }

        .stat-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #4b5563;
          margin: 0 0 0.5rem 0;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          color: #1f2937;
          margin: 0 0 0.75rem 0;
        }

        .stat-change {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.25rem 0.5rem;
          border-radius: 0.5rem;
        }

        .stat-increase {
          background: #d1fae5;
          color: #065f46;
        }

        .stat-icon {
          width: 4rem;
          height: 4rem;
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
          transition: transform 0.3s ease;
        }

        .stat-card:hover .stat-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 1.5rem;
        }

        .chart-card {
          background: white;
          border-radius: 1rem;
          padding: 1.5rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          transition: all 0.3s ease;
        }

        .chart-card:hover {
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .chart-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0;
        }

        .chart-subtitle {
          font-size: 0.875rem;
          color: #6b7280;
          margin: 0;
        }

        .chart-tabs {
          display: flex;
          background: #f3f4f6;
          border-radius: 0.5rem;
          padding: 0.25rem;
          gap: 0.25rem;
        }

        .chart-tab {
          padding: 0.5rem 1rem;
          background: transparent;
          border: none;
          border-radius: 0.375rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .chart-tab:hover {
          color: #1f2937;
          background: rgba(255, 255, 255, 0.5);
        }

        .chart-tab-active {
          background: white;
          color: #1f2937;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }

        .chart-container {
          width: 100%;
          height: 400px;
        }

        @media (max-width: 768px) {
          .welcome-title {
            font-size: 1.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .charts-grid {
            grid-template-columns: 1fr;
          }

          .chart-container {
            height: 300px;
          }

          .chart-header {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default DashboardHome;